<template>
    <page-controls>
        <template v-slot:left>
            <div class="grow @3xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable" data-test="contact-filters">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find Contacts</span>
                        <vx-badge
                            v-if="filtersCount > 0"
                            :borders="false"
                            size="xs"
                            @click.stop="reset"
                        >
                            {{ pluralized('filter', filtersCount, true )}}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply">
                            <filter-label :canReset="!!filters['filter[search]']" @reset="filters['filter[search]'] = undefined">
                                {{ isLargeTeam(teamId) ? 'Phone or Email' : 'Keyword Search' }}
                            </filter-label>
                            <vx-input

                                type="text"
                                data-test="filters-query"
                                placeholder="Search"
                                v-model="filters['filter[search]']"
                                maxlength="191"
                                size="lg"
                                class="mb-4"
                            >
                                <template v-slot:right>
                                    <font-awesome-icon widthAuto
                                        class="text-xs"
                                        :icon="faMagnifyingGlass"
                                    />
                                </template>
                            </vx-input>

                            <div class="flex gap-2 pt-4">
                                <vx-button
                                    type="button"
                                    size="lg"
                                    color="muted-light"
                                    @click="cancel"
                                >
                                    Cancel
                                </vx-button>
                                <vx-button
                                    data-test="filters-apply"
                                    size="lg"
                                    type="submit"
                                    block
                                >
                                    Apply
                                    <font-awesome-icon widthAuto
                                        :icon="faCircleCheck"
                                    ></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
                <vx-button-group size="lg" class="w-full">
                    <vx-select
                        v-model="filters.sort_field"
                        @update:modelValue="apply"
                        :options="[
                            {
                                value: 'id',
                                label: 'Sort by: Created',
                            },
                        ]"
                    ></vx-select>
                    <vx-button
                        @click.prevent="
                            filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                            apply();
                        "
                        type="button"
                        color="white"
                        shadow
                    >
                        <font-awesome-icon widthAuto
                            :icon="
                                filters.sort_dir === 'asc'
                                    ? faArrowUpWideShort
                                    : faArrowDownWideShort
                            "
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>
        <template v-slot:right>
            <div class="grid grid-cols-2 gap-4">
                <vx-button
                    block
                    data-test="bulk-remove-btn"
                    @click.prevent="emit('bulkRemove')"
                    size="lg"
                    :disabled="disableButtons"
                    color="primary"
                >
                    Remove Selected
                    <font-awesome-icon widthAuto :icon="faCircleMinus"></font-awesome-icon>
                </vx-button>
                <vx-button
                    block
                    data-test="bulk-add-btn"
                    @click.prevent="emit('bulkAdd')"
                    size="lg"
                    :disabled="disableButtons"
                    color="primary"
                >
                    Add Selected
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </page-controls>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import {
    VxBadge,
    VxButton,
    VxButtonGroup,
    VxFilterable,
    VxInput,
    VxSelect,
} from '@voxie/frontend-components';
import {
    faCircleCheck,
    faMagnifyingGlass,
    faCircleXmark,
    faArrowDownWideShort,
    faArrowUpWideShort, faCirclePlus, faCircleMinus,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageControls from '~/components/general/PageControls.vue';
import { isLargeTeam } from '~/utils/team';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';

const teamId = Spark.state.currentTeam.id;

const filters = ref({});
const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    disableButtons: {
        type: Boolean,
        required: true,
    },
});
watch(() => props.modelValue, (modelValue) => {
    filters.value = {...modelValue};
}, {
    deep: true,
    immediate: true,
})

const emit = defineEmits(['update:modelValue', 'apply', 'reset', 'bulkAdd', 'bulkRemove']);

const active = ref(false);

const filtersCount = computed(() => (props.modelValue['filter[search]'] ? 1 : 0));

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...props.modelValue }
    active.value = false;
}

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
}

const toggleFilterable = (event) => {
    active.value = event;
};
</script>
